import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { X, Pen } from 'lucide-react';
import { z } from 'zod';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api/client';
import { getApiErrorMessage, getLocalizedName } from '@/lib/utils';
import { useRoles } from '@/features/roles/hooks/useRoles';
import type { RoleType } from '@/features/roles/types';
import { useHourlyPackageMutations } from '../hooks';

// Roles that book billable work — the only ones a package needs overtime rates for.
const BILLABLE_ROLE_TYPES: RoleType[] = ['MD', 'PARTNER', 'LEAD', 'STAFF'];

const rateSchema = z.object({
  roleId: z.coerce.number().int().positive(),
  rate: z.coerce.number().min(0, 'Rate must be 0 or more'),
});

const hourlyPackageSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  hours: z.coerce.number().min(0, 'Hours must be 0 or more'),
  price: z.coerce.number().min(0, 'Price must be 0 or more'),
  overTimeHourlyCost: z.coerce.number().min(0, 'Overtime cost must be 0 or more'),
  rates: z.array(rateSchema).default([]),
});

type FormValues = z.infer<typeof hourlyPackageSchema>;

interface HourlyPackageActionProps {
  id?: number | string;
}

export function HourlyPackageAction({ id }: HourlyPackageActionProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const { createMutation, updateMutation } = useHourlyPackageMutations();

  const { data: rolesData } = useRoles({ limit: 100, page: 0, status: 'ACTIVE' });
  const billableRoles = useMemo(
    () =>
      (rolesData?.results ?? []).filter((r) =>
        BILLABLE_ROLE_TYPES.includes(r.type)
      ),
    [rolesData]
  );
  // Rates loaded from the package on edit, merged into the fixed rows once roles arrive.
  const [loadedRates, setLoadedRates] = useState<
    { roleId: number; rate: number }[]
  >([]);

  const form = useForm<FormValues>({
    resolver: zodResolver(hourlyPackageSchema),
    defaultValues: {
      name: '',
      hours: 0,
      price: 0,
      overTimeHourlyCost: 0,
      rates: [],
    },
  });

  const showMutation = useMutation({
    mutationFn: async (showId: number | string) => {
      return api.get(`/hourly-packages/${showId}`);
    },
    onSuccess: (data: Record<string, unknown>) => {
      form.reset({
        name: data.name as string ?? '',
        hours: data.hours as number ?? 0,
        price: data.price as number ?? 0,
        overTimeHourlyCost: data.overTimeHourlyCost as number ?? 0,
        rates: [],
      });
      setLoadedRates(
        Array.isArray(data.rates)
          ? (data.rates as { roleId: number; rate: number }[]).map((r) => ({
              roleId: r.roleId,
              rate: r.rate,
            }))
          : []
      );
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  useEffect(() => {
    if (open && id) {
      showMutation.mutate(id);
    }
    if (!open) {
      form.reset({
        name: '',
        hours: 0,
        price: 0,
        overTimeHourlyCost: 0,
        rates: [],
      });
      setLoadedRates([]);
    }
  }, [open, id]);

  // Build one rate row per billable role, prefilled with any existing rate.
  useEffect(() => {
    if (!open || billableRoles.length === 0) return;
    form.setValue(
      'rates',
      billableRoles.map((role) => ({
        roleId: role.id,
        rate: loadedRates.find((r) => r.roleId === role.id)?.rate ?? 0,
      }))
    );
  }, [open, billableRoles, loadedRates]);

  const rateRows = form.watch('rates');

  const onSubmit = (values: FormValues) => {
    if (id) {
      updateMutation.mutate(
        { id, data: values },
        { onSuccess: () => setOpen(false) }
      );
    } else {
      createMutation.mutate(values, { onSuccess: () => setOpen(false) });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        {id ? (
          <Button size="sm" variant="outline" className="rounded-r-none">
            <Pen />
          </Button>
        ) : (
          <Button>
            {t('new')} <span className="lowercase">{t('hourlyPackage')}</span>
          </Button>
        )}
      </AlertDialogTrigger>

      <AlertDialogContent className="p-6 max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b pb-4 border-[#D9D9D9]">
          <AlertDialogTitle className="text-xl font-semibold">
            {id ? t('update') : t('new')} <span className="lowercase">{t('hourlyPackage')}</span>
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <button onClick={() => setOpen(false)} aria-label={t('closeDialog')}>
              <X size={24} />
            </button>
          </AlertDialogDescription>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-4 flex flex-col gap-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('name')} <sup className="text-red-500">*</sup></FormLabel>
                  <FormControl>
                    <Input placeholder={t('enterName')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="hours"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('hours')} <sup className="text-red-500">*</sup></FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('price')} <sup className="text-red-500">*</sup></FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="overTimeHourlyCost"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('overTimeHourlyCost')}</FormLabel>
                  <FormControl>
                    <Input type="number" placeholder="0" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {billableRoles.length > 0 &&
              rateRows.length === billableRoles.length && (
                <div className="space-y-3">
                  <div>
                    <FormLabel>{t('rates')}</FormLabel>
                    <p className="text-muted-foreground mt-1 text-xs">
                      {t('perRoleOvertimeRatesHint')}
                    </p>
                  </div>
                  <div className="space-y-2">
                    {billableRoles.map((role, index) => (
                      <div key={role.id} className="flex items-center gap-3">
                        <span className="flex-1 text-sm">
                          {getLocalizedName(role, i18n.language)}
                        </span>
                        <FormField
                          control={form.control}
                          name={`rates.${index}.rate`}
                          render={({ field }) => (
                            <FormItem className="w-32">
                              <FormControl>
                                <Input
                                  type="number"
                                  min={0}
                                  placeholder="0"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

            <AlertDialogFooter className="flex justify-end gap-2 mt-4">
              <AlertDialogCancel onClick={() => setOpen(false)}>
                {t('cancel')}
              </AlertDialogCancel>
              <Button type="submit" disabled={isPending}>
                {isPending ? t('saving') : t('save')}
              </Button>
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
